// Create new file: src/pages/admin/LeaveManagementDashboard/RollBackTab.tsx

import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Typography,
  IconButton,
  Tooltip
} from '@mui/material';
import { Undo } from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosConfig';

interface LeaveRequest {
  _id: string;
  personnelId: {
    _id: string;
    name: string;
    email: string;
    currentLeaveBalance?: {
      annualLeave: number;
      timeInLieu: number;
    };
  };
  type: 'annual-leave' | 'time-lieu';
  dateStart: string;
  hoursRequested?: number;
  reason?: string;
  status: 'approved';
  decisionDate?: string;
}

const RollBackTab: React.FC = () => {
  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchApprovedRequests();
  }, []);

  const fetchApprovedRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axiosInstance.get('/api/leave-requests');
      const allRequests = response.data;
      
      // Filter: only approved requests
      const approvedRequests = allRequests.filter((req: LeaveRequest) => 
        req.status === 'approved'
      );
      
      setRequests(approvedRequests);
      setLoading(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch approved requests');
      setLoading(false);
    }
  };

  const handleRevert = async (request: LeaveRequest) => {
    if (!window.confirm(
      `Revert approval for ${request.personnelId.name}?\n\n` +
      `This will credit back ${request.hoursRequested || 1} ${request.type === 'time-lieu' ? 'hours' : 'day(s)'}.`
    )) return;
    
    try {
      await axiosInstance.patch(`/api/leave-requests/${request._id}/revert`);
      
      setSuccessMessage(
        `✅ Reverted ${request.type === 'time-lieu' ? 'TIL' : 'AL'} for ${request.personnelId.name}. ` +
        `Balance restored: ${request.hoursRequested || 1} ${request.type === 'time-lieu' ? 'hours' : 'day(s)'}`
      );
      setTimeout(() => setSuccessMessage(null), 8000);
      
      fetchApprovedRequests();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to revert request');
      setTimeout(() => setError(null), 5000);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      
      return date.toLocaleDateString('en-GB', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch (e) {
      return 'Invalid Date';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 2, fontSize: '1rem', fontWeight: 500 }} 
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}
      
      {successMessage && (
        <Alert 
          severity="success" 
          sx={{ mb: 2, fontSize: '1rem', fontWeight: 500 }} 
          onClose={() => setSuccessMessage(null)}
        >
          {successMessage}
        </Alert>
      )}

      {requests.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No approved requests to roll back
          </Typography>
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Staff</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Hours</TableCell>
                <TableCell>Approved Date</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((request) => (
                <TableRow key={request._id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {request.personnelId.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={request.type === 'annual-leave' ? 'AL' : 'TIL'}
                      size="small"
                      color={request.type === 'annual-leave' ? 'primary' : 'secondary'}
                    />
                  </TableCell>
                  <TableCell>{formatDate(request.dateStart)}</TableCell>
                  <TableCell>
                    {request.hoursRequested ? `${request.hoursRequested}h` : '1 day'}
                  </TableCell>
                  <TableCell>
                    {request.decisionDate ? formatDate(request.decisionDate) : '-'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                      {request.reason || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Revert (Credit Back Balance)">
                      <IconButton
                        size="small"
                        color="warning"
                        onClick={() => handleRevert(request)}
                      >
                        <Undo />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default RollBackTab;